import { useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_ROBOT_RETURN_LOCATION,
  DEPLOYMENT_RETURN_REASON_LABELS,
  DeploymentReturnReason,
  ROBOT_RETURN_LOCATION_CHOICES,
  ROBOT_RETURN_LOCATION_LABELS,
  RobotStatus,
  formatRobotShortId,
  type CalendarReplacementDto,
  type OwnerEntity,
  type RobotReturnLocation,
} from '@bhops/shared';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { useOptionalToast } from '@/components/Toast';
import { robotsApi } from '@/api/robots';
import { scheduleDatesApi } from '@/api/scheduleDates';
import { extractApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { otherEntityRobotLabel, robotUsableForUser } from '@/pages/robots/robot-entity';

// 통합 캘린더 로봇 교체 일정 → [교체 완료](2026-10-20 오너 — "일정에서 바로 교체 완료". 일정을 누르거나
// 우클릭). 고객사 화면 [로봇 교체]와 같은 거래를 서버가 로봇마다 한다: 뺄 로봇 회수 + 넣을 로봇을 같은
// 매장에 배치(계약 승계 포함), 그리고 그 교체 오더를 '해결'로 닫는다.
// 여러 대 교체(2026-10-20)면 로봇마다 한 줄 — 체크한 로봇만 이번에 끝낸다(나머지는 다음에). 넣을 로봇은
// 제조팀이 배정했으면 그 로봇이 먼저 골라져 있고, 아니면 출고 대기 로봇 중에서 고른다.

export function CompleteReplacementModal({
  replacement: r,
  date: initialDate,
  onClose,
}: {
  replacement: CalendarReplacementDto;
  /** 캘린더 칸의 날짜(YYYY-MM-DD) — 교체한 날 기본값. */
  date: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useOptionalToast();
  const pending = r.items.filter((i) => !i.done);
  const [picked, setPicked] = useState<Record<string, string>>(() =>
    Object.fromEntries(pending.map((i) => [i.id, i.newRobot?.id ?? ''])),
  );
  // 이번에 끝낼 로봇 — 기본은 나가 있는 로봇 전부.
  const [on, setOn] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(pending.map((i) => [i.id, !!i.deploymentId])),
  );
  const [date, setDate] = useState(initialDate);
  const [returnReason, setReturnReason] = useState<DeploymentReturnReason>(
    DeploymentReturnReason.SWAP_DEFECT,
  );
  const [returnLocation, setReturnLocation] = useState<RobotReturnLocation>(
    DEFAULT_ROBOT_RETURN_LOCATION,
  );
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const candidatesQ = useQuery({
    queryKey: ['robots', 'swap-candidates'],
    queryFn: () => robotsApi.list({ status: RobotStatus.READY_TO_SHIP, pageSize: 200 }),
    staleTime: 10_000,
  });
  // 오너 결정 2026-10-21 — 허니컴로보틱스 로봇도 비욘드허니컴 고객사에 배치. 서버 교체(로봇마다
  // deployments swap)는 '이 고객사 법인에 쓸 수 있는 로봇'을 받는다(canUseRobotAt): 비욘드허니컴
  // 고객사 ← 두 법인 로봇 모두, 허니컴로보틱스 고객사 ← 허니컴로보틱스 로봇만. 이 일정 응답에는
  // 고객사의 법인이 실리지 않으므로 robotUsableForUser 로 거른다 — 관리자가 아니면 그 사람의
  // 고객사는 언제나 자기 법인 것이고, 관리자는 거르지 않고 서버에 맡긴다(못 쓰는 로봇이면 그 줄의
  // 까닭이 아래 오류 칸에 뜬다). 소유 법인은 바뀌지 않으므로 다른 법인 로봇에는 법인 이름을 붙인다.
  const authUser = useAuthStore((s) => s.user);
  const candidates = (candidatesQ.data?.items ?? []).filter((x) => robotUsableForUser(authUser, x));
  const multi = r.items.length > 1;
  const chosen = pending.filter((i) => on[i.id] && i.deploymentId);
  const chosenRobots = chosen.map((i) => picked[i.id]).filter(Boolean);
  const duplicate = new Set(chosenRobots).size !== chosenRobots.length;

  const save = useMutation({
    mutationFn: () =>
      scheduleDatesApi.completeReplacement(r.id, {
        date,
        items: chosen.map((i) => ({ id: i.id, newRobotId: picked[i.id] })),
        returnReason,
        returnLocation,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      }),
    onSuccess: async (res) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
        qc.invalidateQueries({ queryKey: ['service-orders'] }),
        qc.invalidateQueries({ queryKey: ['deployments'] }),
        qc.invalidateQueries({ queryKey: ['robots'] }),
        qc.invalidateQueries({ queryKey: ['customer', r.customerId] }),
      ]);
      const failed = res.results.filter((x) => !x.ok);
      if (failed.length > 0) {
        // 일부만 됐다 — 창을 닫지 않고 실패한 로봇의 까닭을 그 자리에 보인다.
        setError(failed.map((x) => x.error ?? '교체하지 못했습니다').join('\n'));
        return;
      }
      if (res.closed) {
        toast?.success(
          `${r.customerName} 로봇 ${res.results.length}대 교체를 마쳤습니다. 1분 안에 구글 캘린더에도 반영됩니다.`,
          '교체 완료',
        );
      } else {
        toast?.error(
          '교체는 했지만 닫지 못한 서비스 오더가 있습니다. 서비스 오더 화면에서 직접 닫아 주세요.',
          '교체 완료',
        );
      }
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  const canSave = chosen.length > 0 && chosen.every((i) => !!picked[i.id]) && !duplicate && !!date;

  return (
    <Modal
      open
      onClose={onClose}
      title="로봇 교체 완료"
      width={multi ? 560 : 480}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            취소
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={!canSave || save.isPending}
            data-testid="replace-complete-save"
          >
            {save.isPending ? '교체 중…' : multi ? `${chosen.length}대 교체 완료` : '교체 완료'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={summary} data-testid="replace-complete-summary">
          <b>{r.customerName}</b>
          <span>
            로봇 {r.items.length}대 교체
            {r.items.length > pending.length
              ? `, ${r.items.length - pending.length}대는 이미 마쳤습니다`
              : ''}
          </span>
        </div>

        <div style={field}>
          <span style={labelSt}>회수할 로봇과 대신 설치할 로봇</span>
          {pending.map((i) => {
            const out = !!i.deploymentId;
            // 다른 줄에서 이미 고른 로봇은 후보에서 뺀다.
            const taken = new Set(
              chosen
                .filter((c) => c.id !== i.id)
                .map((c) => picked[c.id])
                .filter(Boolean),
            );
            return (
              <div key={i.id} style={itemRow} data-testid={`replace-complete-item-${i.id}`}>
                {multi && (
                  <input
                    type="checkbox"
                    checked={!!on[i.id] && out}
                    disabled={!out}
                    onChange={(e) => setOn((prev) => ({ ...prev, [i.id]: e.target.checked }))}
                    aria-label={`${i.oldRobot.label} 이번에 교체`}
                    data-testid={`replace-complete-check-${i.id}`}
                  />
                )}
                <span style={oldRobotSt}>
                  <Icon name="undo" size={13} aria-hidden /> {i.oldRobot.label}
                  {i.oldRobot.modelName ? ` · ${i.oldRobot.modelName}` : ''}
                </span>
                <span aria-hidden>→</span>
                {out ? (
                  <select
                    value={picked[i.id] ?? ''}
                    onChange={(e) => setPicked((prev) => ({ ...prev, [i.id]: e.target.value }))}
                    style={{ ...input, flex: 1, minWidth: 0 }}
                    disabled={multi && !on[i.id]}
                    aria-label={`${i.oldRobot.label} 대신 설치할 로봇`}
                    data-testid={
                      multi ? `replace-complete-robot-${i.id}` : 'replace-complete-robot'
                    }
                  >
                    <option value="">설치할 로봇을 고르세요</option>
                    {i.newRobot && (
                      <option value={i.newRobot.id}>
                        {i.newRobot.label}
                        {i.newRobot.modelName ? ` · ${i.newRobot.modelName}` : ''} · 제조팀 배정
                      </option>
                    )}
                    {candidates
                      .filter((x) => x.id !== i.newRobot?.id && !taken.has(x.id))
                      .map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.serialShort
                            ? formatRobotShortId(x.serialShort)
                            : (x.serialLong ?? x.id)}{' '}
                          · {x.model.displayName}
                          {entitySuffix(x.ownerEntity, authUser?.ownerEntity)}
                        </option>
                      ))}
                  </select>
                ) : (
                  <span style={blockedSt} data-testid="replace-complete-blocked">
                    이 고객사에 나가 있지 않습니다
                  </span>
                )}
              </div>
            );
          })}
          {!candidatesQ.isLoading &&
            candidates.length === 0 &&
            pending.some((i) => !i.newRobot) && (
              <span style={hint}>
                출고 대기 상태의 로봇이 없습니다. 먼저 제조팀에서 출고 대기로 바꿔 주세요.
              </span>
            )}
          {pending.some((i) => !i.deploymentId) && (
            <span style={hint}>
              나가 있지 않은 로봇은 이미 뺐을 수 있습니다. 고객사 화면에서 확인해 주세요.
            </span>
          )}
        </div>

        <label style={field}>
          <span style={labelSt}>교체한 날</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={input}
            data-testid="replace-complete-date"
          />
        </label>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ ...field, flex: '1 1 180px' }}>
            <span style={labelSt}>회수 사유</span>
            <select
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value as DeploymentReturnReason)}
              style={input}
            >
              {Object.entries(DEPLOYMENT_RETURN_REASON_LABELS).map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label style={{ ...field, flex: '1 1 180px' }}>
            <span style={labelSt}>회수한 로봇을 어디로</span>
            <select
              value={returnLocation}
              onChange={(e) => setReturnLocation(e.target.value as RobotReturnLocation)}
              style={input}
            >
              {ROBOT_RETURN_LOCATION_CHOICES.map((v) => (
                <option key={v} value={v}>
                  {ROBOT_RETURN_LOCATION_LABELS[v]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label style={field}>
          <span style={labelSt}>메모 (선택)</span>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            placeholder="교체 사유나 특이사항"
            style={input}
          />
        </label>

        <span style={hint}>
          회수할 로봇의 출고가 끝나고 설치할 로봇이 같은 매장에 나가며, 계약도 이어집니다. 교체
          작업은 &lsquo;해결&rsquo;로 닫힙니다. 잘못했으면 일정을 우클릭해 [삭제]하면 교체가
          되돌아갑니다.
        </span>

        {duplicate && (
          <div role="alert" style={errorSt}>
            같은 로봇을 두 자리에 넣을 수 없습니다.
          </div>
        )}
        {error && (
          <div
            role="alert"
            style={{ ...errorSt, whiteSpace: 'pre-line' }}
            data-testid="replace-complete-error"
          >
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * <option> 안에는 요소를 못 넣으므로 다른 법인 로봇의 법인 이름을 괄호로 덧붙인다(2026-10-21).
 * 고객사 법인을 이 응답이 모르므로 내 계정의 법인과 견준다.
 */
function entitySuffix(
  robotEntity: OwnerEntity | null | undefined,
  myEntity: OwnerEntity | null | undefined,
): string {
  const label = otherEntityRobotLabel(robotEntity, myEntity);
  return label ? ` (${label})` : '';
}

const summary: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: '10px 12px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-sunken)',
  fontSize: 13,
};
const itemRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 };
const oldRobotSt: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  minWidth: 120,
  fontFamily: 'var(--font-mono)',
};
const blockedSt: CSSProperties = { flex: 1, fontSize: 12, color: 'var(--fg-danger)' };
const field: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };
const labelSt: CSSProperties = { fontSize: 13, fontWeight: 600 };
const input: CSSProperties = {
  width: '100%',
  height: 36,
  padding: '6px 10px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
  boxSizing: 'border-box',
};
const hint: CSSProperties = { fontSize: 12, color: 'var(--fg-muted)' };
const errorSt: CSSProperties = {
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-danger-subtle)',
  color: 'var(--fg-danger)',
  fontSize: 13,
};
